import type { Locale } from '@enmon/commons';
import { ConfigProvider } from 'antd';
import { useState } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
import { InventoryAppServicesProvider } from '@/app/services/InventoryAppServicesContext';
import { StartupGate } from '@/app/session/StartupGate';
import { AppShell } from '@/app/shell/AppShell';
import { inventorizeFieldPath, inventorizeReviewPath, inventoryRoutes } from '@/app/shell/routes';
import { useFieldAutoSync } from '@/features/sync/engine/useFieldAutoSync';
import { useCreateInventorizationDraft } from '@/features/today/useCreateInventorizationDraft';
import { getInventoryLocale, LanguageProvider, setInventoryLocale } from '@/providers/i18';
import { InventorizationView } from '@/routers/inventorization/components/InventorizationView';
import { MetersView } from '@/routers/meters/components/MetersView';
import { ScanView } from '@/routers/scan/components/ScanView';
import { SettingsView } from '@/routers/settings/components/SettingsView';
import { SyncCenterView } from '@/routers/sync/components/SyncCenterView';
import { TodayView } from '@/routers/today/components/TodayView';
import { useBrowserDetection } from '@/shared/browser/useBrowserDetection';
import type { MeterSearchResult } from '@/shared/meter-identification';
import { antdTheme } from '@/theme';

interface InventorizeRouteParams {
  fieldId?: string;
  sessionId: string;
}

export function App() {
  useBrowserDetection();
  const autoSyncStatus = useFieldAutoSync();
  const [locale, setLocaleState] = useState<Locale>(() => getInventoryLocale());

  function handleLocaleChange(nextLocale: Locale) {
    setInventoryLocale(nextLocale);
    setLocaleState(nextLocale);
  }

  return (
    <LanguageProvider locale={locale}>
      <ConfigProvider theme={antdTheme}>
        <InventoryAppServicesProvider>
          <BrowserRouter>
            <Switch>
              <Route exact path={inventoryRoutes.settings}>
                <SettingsRoute autoSyncStatus={autoSyncStatus} locale={locale} onLocaleChange={handleLocaleChange} />
              </Route>
              <Route>
                <StartupGate locale={locale} onLocaleChange={handleLocaleChange}>
                  <InventoryRouter
                    autoSyncStatus={autoSyncStatus}
                    locale={locale}
                    onLocaleChange={handleLocaleChange}
                  />
                </StartupGate>
              </Route>
            </Switch>
          </BrowserRouter>
        </InventoryAppServicesProvider>
      </ConfigProvider>
    </LanguageProvider>
  );
}

function SettingsRoute({
  autoSyncStatus,
  locale,
  onLocaleChange,
}: {
  autoSyncStatus: ReturnType<typeof useFieldAutoSync>;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
}) {
  const history = useHistory();

  return (
    <SettingsView
      autoSyncStatus={autoSyncStatus}
      locale={locale}
      onBack={() => history.push(inventoryRoutes.root)}
      onLocaleChange={onLocaleChange}
    />
  );
}

function InventoryRouter({
  autoSyncStatus,
  locale,
  onLocaleChange,
}: {
  autoSyncStatus: ReturnType<typeof useFieldAutoSync>;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
}) {
  const history = useHistory();
  const { createDraft, isCreating } = useCreateInventorizationDraft();

  async function openMeterReview(meter: MeterSearchResult) {
    const session = await createDraft({ matchedMeter: meter });

    if (session) {
      history.push(inventorizeReviewPath(session.id));
    }
  }

  return (
    <AppShell autoSyncStatus={autoSyncStatus} locale={locale} onLocaleChange={onLocaleChange}>
      <Switch>
        <Route exact path={inventoryRoutes.root}>
          <TodayView
            onOpenQueuedMeter={sessionId => history.push(inventorizeReviewPath(sessionId))}
            onOpenScan={() => history.push(inventoryRoutes.scan)}
          />
        </Route>
        <Route exact path={inventoryRoutes.scan}>
          <ScanView
            onCreateDraft={async input => {
              const session = await createDraft(input);

              if (session) {
                history.push(inventorizeReviewPath(session.id));
              }

              return session;
            }}
            onOpenQueuedMeter={sessionId => history.push(inventorizeReviewPath(sessionId))}
          />
        </Route>
        <Route exact path={inventoryRoutes.meters}>
          <MetersView
            isOpeningMeter={isCreating}
            onOpenMeter={openMeterReview}
            onOpenQueuedMeter={sessionId => history.push(inventorizeReviewPath(sessionId))}
          />
        </Route>
        <Route exact path={inventoryRoutes.sync}>
          <SyncCenterView />
        </Route>
        <Route exact path={inventoryRoutes.inventorize}>
          <InventorizeReviewRedirect />
        </Route>
        <Route exact path={inventoryRoutes.inventorizePhoto}>
          <InventorizeReviewRedirect />
        </Route>
        <Route exact path={inventoryRoutes.inventorizeField}>
          <InventorizeRoute />
        </Route>
        <Route exact path={inventoryRoutes.inventorizeReview}>
          <InventorizeRoute />
        </Route>
        <Route>
          <Redirect to={inventoryRoutes.root} />
        </Route>
      </Switch>
    </AppShell>
  );
}

function InventorizeRoute() {
  const { fieldId, sessionId } = useParams<InventorizeRouteParams>();
  const history = useHistory();

  return (
    <InventorizationView
      activeFieldId={fieldId ?? null}
      onBackToScan={() => history.push(inventoryRoutes.scan)}
      onCloseField={() => history.push(inventorizeReviewPath(sessionId))}
      onOpenField={field => history.push(inventorizeFieldPath(sessionId, field.id))}
      onQueuedToToday={() => history.push(inventoryRoutes.root)}
      sessionId={sessionId}
    />
  );
}

function InventorizeReviewRedirect() {
  const { sessionId } = useParams<InventorizeRouteParams>();

  return <Redirect to={inventorizeReviewPath(sessionId)} />;
}
